import {APP_CONFIG} from '../config';
import type {Coordinate} from '../types';

const EARTH_RADIUS_KM = 6371;

const toRadians = (value: number): number => (value * Math.PI) / 180;

export const calculateDistanceKm = (from: Coordinate, to: Coordinate): number => {
  const lat1 = toRadians(from.latitude);
  const lat2 = toRadians(to.latitude);
  const deltaLat = toRadians(to.latitude - from.latitude);
  const deltaLng = toRadians(to.longitude - from.longitude);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
};

export const formatCoordinate = (coordinate: Coordinate): string => {
  const {latitude, longitude} = coordinate;
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
};

export const reverseGeocode = async (coordinate: Coordinate): Promise<string | null> => {
  const apiKey = APP_CONFIG.maps?.googleApiKey ?? '';
  if (!apiKey.trim()) {
    return null;
  }

  const {latitude, longitude} = coordinate;
  const url =
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}` +
    `&key=${apiKey}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (Array.isArray(data?.results) && data.results.length > 0) {
      const result = data.results[0];
      if (typeof result?.formatted_address === 'string') {
        return result.formatted_address;
      }
    }
  } catch (error) {
    return null;
  }

  return null;
};

export const resolveAddressForCoordinate = async (
  coordinate: Coordinate,
): Promise<string> => {
  const resolved = await reverseGeocode(coordinate);
  return resolved ?? formatCoordinate(coordinate);
};
